import { FavoritesData, WatchHistory } from '@inplayer-org/inplayer.js';
import { SerializedFavorite } from '@jwp/ott-common/types/favorite';
import { SerializedWatchHistoryItem } from '@jwp/ott-common/types/watchHistory';

import { getFavouriteResponse, getWatchHistoryResponse } from '../../helpers/AuthHelpers';

import {
  getCustomFavorites,
  getCustomWatchHistory,
  updateCustomFavorites,
  updateCustomWatchHistory,
} from './CustomAccountService';

export {
  deleteCustomFavorites,
  getCustomFavorites,
  getCustomWatchHistory,
  updateCustomFavorites,
  updateCustomWatchHistory,
  deleteCustomWatchHistory,
} from './CustomAccountService';

const formatFavorite = (favorite: FavoritesData): SerializedFavorite => {
  return {
    mediaid: favorite.media_id,
  };
};

const formatHistoryItem = (history: WatchHistory): SerializedWatchHistoryItem => {
  return {
    mediaid: history.media_id,
    progress: history.progress,
  };
};

const fetchUserFavorites = async (): Promise<SerializedFavorite[]> => {
  try {
    const response = await getCustomFavorites();
    const result = getFavouriteResponse(response);
    return result?.collection?.map(formatFavorite) || [];
  } catch (error) {
    console.error('Error fetching favorites:', error);
    throw new Error('Failed to fetch user favorites');
  }
};

const syncFavorites = async (favorites: any): Promise<void> => {
  const current = await fetchUserFavorites();
  const currentFavoriteIds = current.map((e: any) => e.mediaid) || [];
  const payloadFavoriteIds = favorites.map((e: any) => e.mediaid);
  try {
    await Promise.allSettled(
      payloadFavoriteIds.map((mediaId: string) => {
        return !currentFavoriteIds.includes(mediaId) ? updateCustomFavorites(mediaId) : Promise.resolve();
      }),
    );

    await Promise.allSettled(
      currentFavoriteIds.map((mediaId: string) => {
        return !payloadFavoriteIds.includes(mediaId) ? deleteCustomFavorites(mediaId) : Promise.resolve();
      }),
    );
  } catch (error) {
    console.error('Error syncing favorites:', error);
    throw new Error('Failed to sync favorites');
  }
};

const fetchUserWatchHistory = async (): Promise<SerializedFavorite[]> => {
  try {
    const watchHistoryData = await getCustomWatchHistory();
    const result = getWatchHistoryResponse(watchHistoryData);
    return result?.collection?.map(formatHistoryItem) || [];
  } catch (error) {
    console.error('Error fetching Watch history:', error);
    throw new Error('Failed to fetch Watch history');
  }
};

let pendingSyncPromise: Promise<void> | null = null;

const syncWatchHistory = async (history: { mediaid: string; progress: number }[]): Promise<void> => {
  if (pendingSyncPromise) {
    return pendingSyncPromise;
  }

  pendingSyncPromise = (async () => {
    try {
      const current = await fetchUserWatchHistory();
      const savedHistory = current.map((e: any) => e.mediaid) || [];
      

      await Promise.allSettled(
        history?.map(({ mediaid, progress }: { mediaid: string; progress: number }) => {
          if (!savedHistory.includes(mediaid) || current.some((e: any) => e.mediaid === mediaid && e.progress !== progress)) {
            return updateCustomWatchHistory(mediaid, progress);
          }
          return Promise.resolve();
        }),
      );
    } catch (error) {
      console.error('Error syncing watch history:', error);
      throw new Error('Failed to sync watch history');
    }
  })();

  try {
    await pendingSyncPromise;
  } finally {
    pendingSyncPromise = null;
  }
};

export { fetchUserFavorites, syncFavorites, fetchUserWatchHistory, syncWatchHistory };
